
import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User, Flower, Clock, MessageSquare, Image as ImageIcon, Trash2 } from 'lucide-react';
import { ChatMessage, Language } from '../types';

interface ChatOverlayProps {
  userName: string;
  messages: ChatMessage[];
  onSendMessage: (text: string, imageUrl?: string) => void;
  onClose: () => void;
  language: Language;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({ userName, messages, onSendMessage, onClose, language }) => {
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const t = {
    pt: { title: 'Conversa com Mestre', placeholder: 'Diga o que você imagina...', send: 'Enviar' },
    en: { title: 'Talk to Master', placeholder: 'Tell us what you imagine...', send: 'Send' }
  }[language];

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() && !selectedImage) return;
    onSendMessage(inputText, selectedImage || undefined);
    setInputText('');
    setSelectedImage(null);
  };

  return (
    <div className="fixed bottom-24 right-8 z-[1000] w-full max-w-[400px] h-[600px] bg-zinc-950/80 backdrop-blur-sm border border-white/10 shadow-[0_30px_100px_rgba(0,0,0,0.8)] flex flex-col animate-in slide-in-from-bottom-8">
      {/* HEADER */}
      <div className="p-6 bg-black/60 border-b border-white/5 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="p-2 bg-[#ff9eb5] shadow-[0_0_10px_rgba(255,158,181,0.5)]">
            <Flower className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-black uppercase text-xs tracking-widest">{t.title}</h3>
            <p className="text-green-500 text-[8px] font-black uppercase tracking-[0.3em] flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /> Online
            </p>
          </div>
        </div>
        <button onClick={onClose} className="text-zinc-600 hover:text-white transition-all">
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* MESSAGES */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar"
      >
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-20">
            <MessageSquare className="w-12 h-12 text-[#ff9eb5] mb-4" />
            <p className="text-[10px] text-white font-black uppercase tracking-widest">Inicie a jornada...</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div 
              key={msg.id} 
              className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
            >
              <div 
                className={`max-w-[85%] p-4 text-sm font-medium leading-relaxed border flex flex-col gap-3 ${
                  msg.sender === 'user' 
                    ? 'bg-zinc-900/90 border-white/5 text-white rounded-l-2xl rounded-tr-2xl' 
                    : 'bg-[#ff9eb5]/10 border-[#ff9eb5]/20 text-[#ff9eb5] rounded-r-2xl rounded-tl-2xl'
                }`}
              >
                {msg.imageUrl && (
                  <img 
                    src={msg.imageUrl} 
                    alt="Chat upload" 
                    className="w-full h-auto rounded-xl border border-white/10"
                  />
                )}
                {msg.text && <span>{msg.text}</span>}
              </div>
              <span className="text-[7px] text-zinc-600 font-black uppercase mt-2 px-1 flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" /> {msg.timestamp}
              </span>
            </div>
          ))
        )}
      </div>

      {/* INPUT */}
      <form onSubmit={handleSubmit} className="p-4 bg-black/40 border-t border-white/5">
        <div className="flex gap-2">
          <input 
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={handleFileChange}
          />
          <button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`p-3 transition-all ${selectedImage ? 'text-[#ff9eb5] bg-[#ff9eb5]/10' : 'text-zinc-600 hover:text-white bg-zinc-900'}`}
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          <input 
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={t.placeholder}
            className="flex-1 bg-black border border-zinc-800 px-4 py-3 text-xs text-white outline-none focus:border-[#ff9eb5] transition-all"
          />
          <button 
            type="submit"
            disabled={!inputText.trim() && !selectedImage}
            className="bg-white hover:bg-[#ff9eb5] text-black hover:text-white p-3 transition-all disabled:opacity-20"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatOverlay;
